import {Capacitor} from "@capacitor/core";
import {Directory, Filesystem, type FileInfo} from "@capacitor/filesystem";
import React, {useEffect, useState} from "react";
import {Button, Column, Row, Text} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {DialogStyle} from "../../../../../Utils/UI/GlobalStyles.js";

/** Root entries shown at the top of the tree-view; each maps to a FileSystem Directory. */
const rootDirs = [
	{key: "Documents", dir: Directory.Documents},
	{key: "Data", dir: Directory.Data},
	{key: "Cache", dir: Directory.Cache},
	{key: "External", dir: Directory.External},
] as const;

/** Which Directory a node's tree descends from, based on its key. (note: root keys contain no "/", so avoid the naive `slice(0, indexOf("/"))` which chops the last character) */
const rootDirByKey = {} as Record<string, Directory>;
for (const {key, dir} of rootDirs) rootDirByKey[key] = dir;
const GetRootDir = (nodeKey: string)=>{
	const slashIndex = nodeKey.indexOf("/");
	return rootDirByKey[slashIndex == -1 ? nodeKey : nodeKey.slice(0, slashIndex)];
};

type FSNode = {
	/** `${rootKey}/${relPath}` for entries, or `${rootKey}` alone for roots. */
	key: string;
	name: string;
	relPath: string; // relative to its root directory ("" for roots)
	isDir: boolean;
	size?: number;
	/** depth in the tree (roots = 0). */
	depth: number;
};

/** Sort entries: folders first, then alphabetical. */
const SortInfos = (infos: FileInfo[])=>infos.slice().sort((a, b)=> {
	if (a.type != b.type) return a.type == "directory" ? -1 : 1;
	return a.name.localeCompare(b.name, undefined, {sensitivity: "base"});
});

/** Compute the bytes for a read result, handling the differing return shapes of the native and web implementations:
 * native: string data is ALWAYS base64-of-the-file-bytes (the JS bridge can only carry strings).
 * web: data is the verbatim stored content (Blob for blob-written files, raw text for UTF8-written files). */
async function GetBytesFromReadResult(data: string | Blob, isWeb: boolean): Promise<Uint8Array<ArrayBuffer>> {
	if (typeof data == "string") {
		// native: interpret the string as base64-of-the-bytes. (on web, string content is the verbatim stored content — typically raw text from UTF8-written files)
		if (!isWeb) {
			const binStr = atob(data);
			const bytes = new Uint8Array(binStr.length);
			for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
			return bytes;
		}
		// web: encode the raw text to bytes directly, no sniffing. Sniffing (base64-charset + length%4) is only *usually* right — it
		// silently mis-decodes text whose content happens to be base64-ish (eg. base64 text, hex strings); prefer fail-fast instead.
		/* kept for reference (previous approach); removed in favor of fail-fast: it silently mis-decoded base64-ish *text* files
		const isProbablyBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(data) && data.length % 4 == 0;
		if (isProbablyBase64) { const s = atob(data); const b = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) b[i] = s.charCodeAt(i); return b; } */
		return new TextEncoder().encode(data);
	}
	// web: Blob content (files written as Blob data); hand back its exact bytes
	return new Uint8Array(await data.arrayBuffer());
}

/** Trigger a browser download of a file (into the user's regular download location/folder). */
async function DownloadFileToBrowser(node: FSNode, dir: Directory) {
	const isWeb = Capacitor.getPlatform() == "web"; // (an alternative is InWeb(0) from Bridge_Native.js; kept local to avoid a layering dependency in this leaf UI)
	const result = await Filesystem.readFile({path: node.relPath, directory: dir});
	const data = result.data as string | Blob;
	const blob = new Blob([await GetBytesFromReadResult(data, isWeb)], {type: "application/octet-stream"});

	const url = URL.createObjectURL(blob);
	try {
		const a = document.createElement("a");
		a.href = url;
		a.download = node.name; // browser places this in the user's configured download folder
		document.body.appendChild(a);
		a.click();
		a.remove();
	} finally {
		// can't revoke immediately, or the download may be cancelled; give the browser a moment
		setTimeout(()=>URL.revokeObjectURL(url), 10000);
	}
}

/** Recursively build the flat display-list, in tree order: for each root, its row followed (depth-first) by the children of every expanded folder. */
async function BuildDisplayNodes(expanded: Set<string>): Promise<FSNode[]> {
	const result: FSNode[] = [];
	const AddChildren = async(node: FSNode)=>{
		if (expanded.has(node.key)) {
			// Note: for root entries, we MUST pass an empty ".path" (not ".", "/", or the display-name "Documents"), so that the
			// web-implementation's getPath() builds the db-key "/DOCUMENTS" (uppercase enum value), which is what entries are stored under (eg. "/DOCUMENTS/LucidFrontier/...").
			const {files} = await Filesystem.readdir({
				path: node.relPath, // "" for roots; "LucidFrontier/DataExports/..." for nested folders
				directory: GetRootDir(node.key),
			});
			for (const info of SortInfos(files)) {
				const childRelPath = node.relPath == "" ? info.name : `${node.relPath}/${info.name}`;
				const childNode: FSNode = {
					key: `${node.key}/${info.name}`,
					name: info.name,
					relPath: childRelPath,
					isDir: info.type == "directory",
					size: info.size,
					depth: node.depth + 1,
				};
				result.push(childNode);
				if (childNode.isDir) await AddChildren(childNode);
			}
		}
	};
	// interleave: each root's row is pushed immediately before its children, so the display-order matches the tree
	for (const root of rootDirs) {
		const rootNode: FSNode = {key: root.key, name: `${root.key}/`, relPath: "", isDir: true, depth: 0};
		result.push(rootNode);
		await AddChildren(rootNode);
	}
	return result;
}

export const VirtualFileSystemViewer = ()=>{
	const [nodes, setNodes] = useState<FSNode[]>([]);
	const [expanded, setExpanded] = useState(new Set<string>());
	const [loadingKey, setLoadingKey] = useState<string|n>(null);
	const [downloadingKey, setDownloadingKey] = useState<string|n>(null);
	const [error, setError] = useState<string|n>(null);

	// initial load: just show the root rows
	useEffect(()=>{
		setNodes(rootDirs.map(root=>({key: root.key, name: `${root.key}/`, relPath: "", isDir: true, depth: 0} as FSNode)));
	}, []);

	const ToggleFolder = async(node: FSNode)=>{
		const newExpanded = new Set(expanded);
		if (expanded.has(node.key)) {
			newExpanded.delete(node.key);
		} else {
			newExpanded.add(node.key);
		}
		setExpanded(newExpanded);
		setLoadingKey(node.key);
		setError(null);
		try {
			setNodes(await BuildDisplayNodes(newExpanded));
		} catch (ex) {
			setError(String(ex));
		}
		setLoadingKey(null);
	};

	const TryDownload = async(node: FSNode)=>{
		setDownloadingKey(node.key);
		setError(null);
		try {
			await DownloadFileToBrowser(node, GetRootDir(node.key));
		} catch (ex) {
			setError(`Failed to download "${node.name}": ${String(ex)}`);
		}
		setDownloadingKey(null);
	};

	return (
		<Column style={DialogStyle({width: 600})}>
			<ScrollView style={{height: 400}} contentStyle={{width: "100%"}}>
				<Column style={{padding: 5, minWidth: 550}}>
					{error != null &&
					<Text sel style={{color: "red", whiteSpace: "pre-wrap"}}>{error}</Text>}
					{nodes.map(node=>(
						<Row key={node.key} sel style={{cursor: node.isDir ? "pointer" : "default", paddingLeft: 5 + node.depth * 20}}
							onClick={node.isDir ? ()=>void ToggleFolder(node) : undefined}>
							<Text>{node.isDir ? (expanded.has(node.key) ? "▼ " : "▶ ") : "• "}{node.name}</Text>
							{!node.isDir && node.size != null && <Text style={{color: "rgba(180,180,180,1)"}}>({node.size.toLocaleString()} bytes)</Text>}
							{loadingKey == node.key && <Text ml={5} style={{color: "rgba(180,180,180,1)"}}>(loading...)</Text>}
							{!node.isDir
								&& <Button ml={5} text={downloadingKey == node.key ? "..." : "Download"} onClick={()=>void TryDownload(node)}/>}
						</Row>
					))}
				</Column>
			</ScrollView>
			<Row mt={5} center>
				<Text style={{color: "rgba(160,160,160,1)"}}>Tap a folder to expand/collapse it. Use the download button to save a file to your download folder.</Text>
			</Row>
		</Column>
	);
};